import DashboardChartFilters from "@components/DashboardChartFilters";
import { DashboardChartCaseTypeSelector } from "@components/DashboardChartFilters/DashboardChartCaseTypeSelector";
import DashboardChartRangeFilter from "@components/DashboardChartFilters/DashboardChartRangeFilter";
import DashboardChartResponsibleFilter from "@components/DashboardChartFilters/DashboardChartResponsibleFilter";
import DashboardChartWrapper from "@components/DashboardChartWrapper";
import DashboardDefaultChartEmptyState from "@components/EmptyStates/DashboardDefaultChartEmptyState";
import { CustomChartStyleWrapper, ToggletipText } from "src/style";
import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { getResponsiblesState } from "@selectors/responsiblesSelectors";
import { sendEventChartUsed } from "@services/dashboardAnalyticEventService";
import { dashboardChartFilterAndActionTypes } from "@utils/constants/dashboard/analyticDashboardEvents";
import {
  DASHBOARD_ACTIVITY_TYPES_DICTIONARY,
  DASHBOARD_CHART_TYPES,
  DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES,
  BAR_AND_LINE_CHART_VISUALIZATION_OPTIONS,
  ChartTypeFilterOption,
} from "@utils/constants/dashboard/dashboardModuleConstants";

import { QueryLoaderWrapper } from "@components/Loaders";
import useCalendarActivityTypes from "@hooks/useCalendarActivityTypes";
import useDefaultResponsibleFilter from "@hooks/useDefaultResponsibleFilter";
import { getChartOptions, MAXIMUM_RESPONSIBLE_NUMBER, MINIMUM_RESPONSIBLE_NUMBER } from "./helpers";
import useDoneActivitiesByResponsibleLineChartQuery from "./useDoneActivitiesByResponsibleLineChartQuery";
import { CalendarActivityTypes } from "@customTypes/filter";
import ChartActions from "@components/ChartActions/ChartActions";
import DashboardChartTypeFilter from "@components/DashboardChartFilters/DashboardChartTypeFilter";

const DoneActivitiesByResponsibleLineChart = () => {
  const ALLOWED_RANGE_TYPES: DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES[] = [
    "LAST_7_DAYS",
    "LAST_30_DAYS",
    "LAST_90_DAYS",
    "LAST_YEAR",
  ];

  const DEFAULT_RESPONSIBLE_FILTER = useDefaultResponsibleFilter(MINIMUM_RESPONSIBLE_NUMBER);

  const responsibleArray = useSelector(getResponsiblesState);

  const [responsibleFilter, setResponsibleFilter] = useState(DEFAULT_RESPONSIBLE_FILTER);
  const [activityType, setActivityTypeFilter] = useState<CalendarActivityTypes>(
    DASHBOARD_ACTIVITY_TYPES_DICTIONARY.ALL.value
  );
  const [rangeFilter, setRangeFilter] =
    useState<DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES>("LAST_7_DAYS");
  const [chartTypeFilter, setChartTypeFilter] = useState<ChartTypeFilterOption>(
    BAR_AND_LINE_CHART_VISUALIZATION_OPTIONS[0]
  );
  const loadOptionALLWithActivityTypes = true;
  const ALLOWED_ACTIVITY_TYPES = useCalendarActivityTypes(loadOptionALLWithActivityTypes);
  const CHART_HEIGHT = 500;
  const CHART_OFFSET = 15;
  const [queryStatus, doneActivitiesByResponsibleResponse, fetchedTime, refetch] =
    useDoneActivitiesByResponsibleLineChartQuery(
      rangeFilter,
      activityType,
      responsibleFilter,
      responsibleArray
    );
  if (!doneActivitiesByResponsibleResponse || !doneActivitiesByResponsibleResponse.series)
    return <></>;

  const series = doneActivitiesByResponsibleResponse?.series ?? [];
  const xAxis = doneActivitiesByResponsibleResponse?.xAxis;
  const groups = doneActivitiesByResponsibleResponse?.groups;
  const noData = doneActivitiesByResponsibleResponse?.noData;

  const options = getChartOptions(xAxis, groups, rangeFilter, chartTypeFilter.value);

  const chartType = chartTypeFilter.value === "LINE" ? "line" : "bar";

  const shouldRenderEmptyState = noData;

  const setRangeFilterAndSendMixPanelEvent = (filter: DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES) => {
    setRangeFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.DONE_ACTIVITIES_BY_RESPONSIBLE,
      dashboardChartFilterAndActionTypes.DATE_RANGE,
      filter
    );
  };

  const setResponsibleFilterAndSendMixPanelEvent = (filter: string[]) => {
    setResponsibleFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.DONE_ACTIVITIES_BY_RESPONSIBLE,
      dashboardChartFilterAndActionTypes.RESPONSIBLE
    );
  };

  const setActivityTypeFilterAndSendMixPanelEvent = (filter: CalendarActivityTypes) => {
    setActivityTypeFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.DONE_ACTIVITIES_BY_RESPONSIBLE,
      dashboardChartFilterAndActionTypes.CALENDAR_ACTIVITY_TYPE,
      filter
    );
  };

  const header = {
    title: "Atividades concluídas por responsável",
    description:
      "Confira a quantidade de atividades concluídas no período selecionado para analisar a produtividade dos responsáveis.",
  };

  const toggleTip = {
    title: "Atividades concluídas por responsável",
    text: (
      <>
        <ToggletipText>
          No gráfico, as atividades são consideradas na data em que deveriam ser concluídas, ou
          seja, como aparecem na agenda do responsável. Independem dos envolvidos, de quem as criou
          e da data em que foi marcada como concluída.
        </ToggletipText>
      </>
    ),
    onClickCallback: () => {
      sendEventChartUsed(
        DASHBOARD_CHART_TYPES.DONE_ACTIVITIES_BY_RESPONSIBLE,
        dashboardChartFilterAndActionTypes.TOGGLE_TIP_OPENED
      );
    },
    callToAction: {
      text: "Acessar central de ajuda",
      callback: () => {
        sendEventChartUsed(
          DASHBOARD_CHART_TYPES.DONE_ACTIVITIES_BY_RESPONSIBLE,
          dashboardChartFilterAndActionTypes.HELP_CENTER_CLICK
        );
        window.open(
          "https://astrea.aurum.com.br/pt-BR/articles/8043389-como-analisar-as-atividades-por-responsavel",
          "_blank"
        );
      },
    },
  };

  return (
    <DashboardChartWrapper
      title={header.title}
      description={header.description}
      toggleTip={toggleTip}
    >
      <ChartActions updatedAt={fetchedTime} refetchFunction={refetch} />

      <DashboardChartFilters>
        <DashboardChartRangeFilter
          allowedRangeTypes={ALLOWED_RANGE_TYPES}
          selectedRange={rangeFilter}
          setRangeFunction={setRangeFilterAndSendMixPanelEvent}
          enableTooltip
        />
        <DashboardChartResponsibleFilter
          currentFilter={responsibleFilter}
          setFilter={setResponsibleFilterAndSendMixPanelEvent}
          maximumSelected={MAXIMUM_RESPONSIBLE_NUMBER}
          minimumSelected={MINIMUM_RESPONSIBLE_NUMBER}
        />
        <DashboardChartCaseTypeSelector
          allowedFilters={ALLOWED_ACTIVITY_TYPES}
          filterSetter={setActivityTypeFilterAndSendMixPanelEvent}
          currentFilter={activityType}
        />
        <DashboardChartTypeFilter
          currentFilter={chartTypeFilter}
          setFilter={setChartTypeFilter}
          allowedOptions={BAR_AND_LINE_CHART_VISUALIZATION_OPTIONS}
        />
      </DashboardChartFilters>
      {shouldRenderEmptyState ? (
        <DashboardDefaultChartEmptyState
          title="Nenhuma atividade concluída encontrada"
          subtitle="Verifique os filtros selecionados."
          chartId={DASHBOARD_CHART_TYPES.DONE_ACTIVITIES_BY_RESPONSIBLE}
          link="https://astrea.aurum.com.br/pt-BR/articles/8043389-como-analisar-as-atividades-por-responsavel"
        />
      ) : (
        <CustomChartStyleWrapper legendMaxWidth>
          <QueryLoaderWrapper
            queryStatus={queryStatus}
            spinnerConfig={{ height: CHART_HEIGHT }}
            skeletonComponent={<Skeleton height={CHART_HEIGHT + CHART_OFFSET} />}
          >
            <ReactApexChart
              key={chartTypeFilter.value}
              options={options}
              series={series}
              height={CHART_HEIGHT}
              type={chartType}
              width="95%"
            />
          </QueryLoaderWrapper>
        </CustomChartStyleWrapper>
      )}
    </DashboardChartWrapper>
  );
};

export default DoneActivitiesByResponsibleLineChart;
